import React from 'react';
import { Save, X, Check, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * SaveBar - Sticky bottom save/discard bar for profile editing
 * Appears when there are unsaved changes
 */
export const SaveBar = ({
  visible = false,
  onSave,
  onDiscard,
  saving = false,
  disabled = false,
  autoSaving = false,
  lastAutoSaved = null
}) => {
  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 inset-x-0 bg-card border-t border-border p-3 md:p-4 z-50 shadow-lg pb-safe"
      data-testid="profile-save-bar"
    >
      <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
        {/* Info text */}
        <div className="flex items-center gap-2 text-sm">
          {autoSaving ? (
            <>
              <Loader2 size={14} className="animate-spin text-muted-foreground" />
              <span className="text-muted-foreground font-medium">Auto-saving...</span>
            </>
          ) : lastAutoSaved ? (
            <>
              <Check size={14} className="text-green-500" />
              <span className="text-muted-foreground font-medium hidden sm:inline">
                Auto-saved at {lastAutoSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
              <span className="text-muted-foreground font-medium sm:hidden">
                Auto-saved
              </span>
            </>
          ) : (
            <>
              <div className="w-2 h-2 rounded-full bg-warning animate-pulse" />
              <span className="text-foreground font-medium hidden sm:inline">
                You have unsaved changes
              </span>
              <span className="text-foreground font-medium sm:hidden">
                Unsaved changes
              </span>
            </>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onDiscard}
            disabled={disabled || saving || autoSaving}
            className="h-9"
            data-testid="profile-discard-button"
          >
            <X size={16} className="mr-1.5" />
            <span className="hidden sm:inline">Discard</span>
          </Button>

          <Button
            type="button"
            onClick={onSave}
            disabled={disabled || saving || autoSaving}
            className="h-9"
            data-testid="profile-save-button"
          >
            <Save size={16} className="mr-1.5" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SaveBar;
